import React, { useMemo, useState } from "react";
import { Search, MessageCircle } from "lucide-react";
import { useSite } from "../context/SiteContext";
import { imgUrl } from "../lib/api";
import { Container, Reveal } from "../components/site/Reveal";
import { PageHero } from "./Bulk";
import { ProductCard } from "../components/home/ProductSections";
import { usePageMeta } from "../lib/seo";

const Prices = () => {
  const { content, products, openChat, t } = useSite();
  const [q, setQ] = useState("");
  const [view, setView] = useState("table");
  const list = useMemo(
    () => products.filter((p) => `${p.name} ${p.brand}`.toLowerCase().includes(q.toLowerCase())).sort((a, b) => b.price - a.price),
    [products, q]
  );
  const chat = (p) => openChat(t("Hi! I'd like a quote for {name} ({size}).", { name: p.name, size: p.size }));
  usePageMeta({ title: "Current Offer Guide | Sealed Testing Supplies | StripWise", description: "See our current offer guide for sealed, unexpired testing supplies such as Dexcom, FreeStyle Libre, Omnipod, OneTouch and Contour. Free quotes, free shipping.", path: "/prices" });
  return (
    <>
      <PageHero eyebrow={t("Price List")} title={t("Current Offer Guide")} text={t("Offers depend on quantity, condition and expiration date. Chat with {brand} for an exact quote.", { brand: content?.brand })} />
      <section className="pb-20">
        <Container>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative w-full sm:max-w-sm">
              <Search size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8a9a97]" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("Search products or brands")} className="w-full rounded-full border border-[#dcebe8] bg-white py-3 pl-11 pr-4 text-[15px] outline-none focus:border-[#0e8c7f]" data-testid="prices-search" />
            </div>
            <div className="flex self-start rounded-full border border-[#dcebe8] bg-white p-1 sm:self-auto">
              {["table", "grid"].map((v) => (
                <button key={v} onClick={() => setView(v)} className={`rounded-full px-5 py-2 text-[14px] font-semibold capitalize transition-colors ${view === v ? "bg-[#0e8c7f] text-white" : "text-[#1f3a36]"}`} data-testid={`prices-view-${v}`}>{t(v)}</button>
              ))}
            </div>
          </div>

          {view === "table" ? (
            <>
            <div className="mt-8 divide-y divide-[#eef5f3] overflow-hidden rounded-2xl border border-[#e3f0ed] bg-white md:hidden" data-testid="prices-list-mobile">
              {list.map((p) => (
                <div key={p.id} className="flex items-center gap-3 p-3">
                  <img src={imgUrl(p.image)} alt="" className="h-14 w-14 shrink-0 rounded-lg border border-[#eef5f3] bg-white object-contain" />
                  <div className="min-w-0 flex-1">
                    <div className="text-[15px] font-semibold leading-tight text-[#0f2e2a]">{p.name}</div>
                    <div className="mt-0.5 text-[12.5px] text-[#5b6d6a]">{[p.brand, p.size, p.expiry].filter(Boolean).join(" · ")}</div>
                    <div className="mt-1 font-jakarta text-[16px] font-extrabold text-[#0e8c7f]">{t("Up to")} ${p.price}</div>
                  </div>
                  <button onClick={() => chat(p)} className="inline-flex shrink-0 items-center gap-1 rounded-full bg-[#e8f8f4] px-3 py-2 text-[13px] font-semibold text-[#0b6f65]" data-testid={`prices-sell-${p.id}`}><MessageCircle size={14} />{t("Sell")}</button>
                </div>
              ))}
            </div>
            <Reveal className="mt-8 hidden overflow-hidden rounded-2xl border border-[#e3f0ed] bg-white md:block">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px] text-left" data-testid="prices-table">
                  <thead className="bg-[#f4fbf9] text-[13px] uppercase tracking-wider text-[#5b6d6a]">
                    <tr><th className="px-5 py-4">{t("Product")}</th><th className="px-5 py-4">{t("Brand")}</th><th className="px-5 py-4">{t("Size")}</th><th className="px-5 py-4">{t("Expiry")}</th><th className="px-5 py-4">{t("Payout")}</th><th className="px-5 py-4"></th></tr>
                  </thead>
                  <tbody>
                    {list.map((p) => (
                      <tr key={p.id} className="border-t border-[#eef5f3] hover:bg-[#fafdfc] transition-colors">
                        <td className="px-5 py-3"><div className="flex items-center gap-3"><img src={imgUrl(p.image)} alt="" className="h-12 w-12 rounded-lg border border-[#eef5f3] object-contain bg-white" /><span className="font-semibold text-[#0f2e2a]">{p.name}</span></div></td>
                        <td className="px-5 py-3 text-[#4b5d5a]">{p.brand}</td>
                        <td className="px-5 py-3 text-[#4b5d5a]">{p.size}</td>
                        <td className="px-5 py-3 text-[#4b5d5a]">{p.expiry}</td>
                        <td className="px-5 py-3 font-jakarta font-extrabold text-[#0e8c7f]">{t("Up to")} ${p.price}</td>
                        <td className="px-5 py-3"><button onClick={() => chat(p)} className="inline-flex items-center gap-1.5 rounded-full bg-[#e8f8f4] px-4 py-2 text-[13.5px] font-semibold text-[#0b6f65] hover:bg-[#0e8c7f] hover:text-white transition-colors"><MessageCircle size={14} />{t("Sell")}</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Reveal>
            </>
          ) : (
            <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {list.map((p) => <ProductCard key={p.id} p={p} onChat={chat} />)}
            </div>
          )}
          {!list.length && <p className="mt-10 text-center text-[#6b7c79]">{t("No products match your search.")}</p>}
        </Container>
      </section>
    </>
  );
};

export default Prices;
